import React, { useState, useEffect, useRef, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterCondition,
  CharacterSheetInterface,
} from "@dndModels";

import API from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import ConditionBar from "@dndComponents/other/ConditionBar";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  char: CharacterSheetInterface;
  disabled?: boolean;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterConditions({ 
  char,
  disabled = false,
  // shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  // Called when the character recalculates — active conditions may have changed,
  // so force a re-render. Re-fetch only if we don't have the conditions list yet.
  const recalculated = useCallback(() => {
    console.log("Conditions - Recalculated");
    setReloading(true);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  }, [fetched, char, recalculated]);

  if (!fetched) {
    return (
      <Grid className="curved_border bg_black_transp">
        <LoadingBox />
      </Grid>
    );
  }

  const enabled = !disabled && !!(
    api.user && (char.owner_id === api.user._id || (char instanceof Character && char.campaign && char.campaign.owner_id === api.user._id))
  );

  // Build active_conditions map from char.conditions + char.condition_immunities
  const active_conditions: { [id: string]: { level: number, source_true_id: string } } = {};
  for (const cc of char.conditions) {
    active_conditions[cc.source_id] = { level: cc.level, source_true_id: cc.true_id };
  }
  // Also include immune conditions so they surface as active chips if desired
  // (immunity is tracked separately via the immune prop, not active_conditions)

  return (
    <Grid className="curved_border bg_black_transp">
      <ConditionBar
        char_int={char}
        active_conditions={active_conditions}
        // starred={char.favorite_conditions}
        starred={[]}
        immune={char.spirit.condition_immunities}
        disabled={!enabled}
        layout="stacked"
        onChange={async (condition_id, level, max_level) => {
          const cond = data_util.conditions.find(o => o._id === condition_id);
          if (!cond) return;
          let cc = char.conditions.find(o => o.source_id === condition_id);
          if (level === 0) {
            if (cc) {
              char_util.removeCharFeatureBasesFor(char, cc);
              char.spirit.conditions = char.conditions.filter(o => o.source_id !== condition_id);
            }
          } else {
            if (!cc) {
              await api.loadObject(cond);
              cc = new CharacterCondition();
              await cc.copy_source({ char, feature_source: cond });
              char.conditions.push(cc);
            }
            cc.level = level;
          }
          char.changed();
          char.subscription_holder.recalculate();
        }}
        onStar={(condition_id) => {
          // if (char.favorite_conditions.includes(condition_id)) {
          //   char.favorite_conditions = char.favorite_conditions.filter(o => o !== condition_id);
          // } else {
          //   char.favorite_conditions.push(condition_id);
          // }
          char.changed();
          char.subscription_holder.recalculate();
        }}
        onImmune={(condition_id) => {
          if (char.spirit.condition_immunities.includes(condition_id)) {
            char.spirit.condition_immunities = char.spirit.condition_immunities.filter(o => o !== condition_id);
          } else {
            char.spirit.condition_immunities.push(condition_id);
          }
          char.changed();
          char.subscription_holder.recalculate();
        }}
      />
    </Grid>
  );
}